import { useEffect, useState, useCallback } from 'react';
import {
  collection, doc, onSnapshot, addDoc, setDoc, deleteDoc, updateDoc, getDocs,
  serverTimestamp, query, orderBy, runTransaction, where,
} from 'firebase/firestore';
import { db } from '../firebase.js';
import { useAuth } from '../useAuth.js';
import { withMembersAdded, withMembersRemoved } from '../lib/contactGroups.js';

// Sends that have been asked for and not finished. The dispatcher reads a
// group's members when it sends, so changing them now would change who an
// approved send reaches without a new approval.
// The same list as the server's smart-group freeze (functions/lib/eshot-in-flight.cases.js; inFlightStatuses.test.js).
export const IN_FLIGHT_STATUSES = ['pending_approval', 'scheduled', 'sending', 'paused'];

export function inFlightSends(sends) {
  return (sends || []).filter((s) => IN_FLIGHT_STATUSES.includes(s.status));
}

export function lockedMessage(sends) {
  const names = sends.map((s) => `"${s.name || 'Untitled broadcast'}" (${String(s.status).replace('_', ' ')})`);
  return `This group is used by ${names.join(', ')}, so its members cannot change until that send is sent or cancelled.`;
}

async function sendsUsing(id) {
  const snap = await getDocs(query(collection(db, 'eshot_sends'), where('audienceId', '==', id)));
  const rows = [];
  snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
  return inFlightSends(rows);
}

/** Refuse when a send that has been asked for still uses this group. */
export async function assertGroupNotInUse(id) {
  const busy = await sendsUsing(id);
  if (busy.length) throw new Error(lockedMessage(busy));
}

/** In-flight sends using this group, kept live while the group is open. */
export function useGroupInUse(id) {
  const [state, setState] = useState({ id: null, sends: [], error: null });
  useEffect(() => {
    if (!id) return undefined;
    const q = query(collection(db, 'eshot_sends'), where('audienceId', '==', id));
    return onSnapshot(q, (snap) => {
      const rows = [];
      snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
      setState({ id, sends: inFlightSends(rows), error: null });
    }, (err) => setState({ id, sends: [], error: err.message || String(err) }));
  }, [id]);
  const ready = state.id === id;
  // Until the first answer arrives, or when it fails, treat the group as locked.
  return { sends: ready ? state.sends : [], locked: !ready || !!state.error || state.sends.length > 0, error: ready ? state.error : null, loading: !ready };
}

// Who was taken out of a built group by hand, newest kept when it is full.
const REMEMBER_MAX = 5000;

export function withRemembered(current, ids) {
  const before = Array.isArray(current) ? current : [];
  return [...new Set([...before, ...(ids || [])])].slice(-REMEMBER_MAX);
}

export function withForgotten(current, ids) {
  const back = new Set(ids || []);
  return (Array.isArray(current) ? current : []).filter((id) => !back.has(id));
}

/**
 * Change a group's members inside a transaction: read the current ids, apply
 * `change`, write ids and count together. Two people editing one group never
 * overwrite each other, and matchCount always equals the stored list.
 */
export async function changeGroupMembers(id, change, alsoSet) {
  await assertGroupNotInUse(id);
  const ref = doc(db, 'eshot_audiences', id);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('That group no longer exists.');
    const data = snap.data();
    if (data.kind !== 'list') throw new Error('That audience is not a group.');
    const contactIds = change(data.contactIds);
    tx.update(ref, {
      ...(alsoSet ? alsoSet(data) : {}),
      contactIds,
      matchCount: contactIds.length,
      matchCountUpdatedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return contactIds.length;
  });
}

/**
 * The groups holding one contact, read with an array-contains query so a
 * contact's panel never pulls every group's member list.
 */
export function useGroupsContaining(contactId) {
  const [state, setState] = useState({ id: null, groups: [], error: null });
  useEffect(() => {
    if (!contactId) return undefined;
    const q = query(collection(db, 'eshot_audiences'), where('contactIds', 'array-contains', contactId));
    return onSnapshot(q, (snap) => {
      const groups = [];
      snap.forEach((d) => { const a = { id: d.id, ...d.data() }; if (a.kind === 'list') groups.push(a); });
      groups.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
      setState({ id: contactId, groups, error: null });
    }, (err) => setState({ id: contactId, groups: [], error: err.message || String(err) }));
  }, [contactId]);
  return { groups: state.id === contactId ? state.groups : [], loading: state.id !== contactId, error: state.id === contactId ? state.error : null };
}

export function useAudiences() {
  const { email } = useAuth();
  const [audiences, setAudiences] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'eshot_audiences'), orderBy('updatedAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const rows = [];
      snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
      setAudiences(rows);
    }, (err) => {
      // Index may not exist yet (orderBy requires one for some collection states).
      console.warn('[useAudiences] ordered query failed, falling back', err.message);
      const sub = onSnapshot(collection(db, 'eshot_audiences'), (snap) => {
        const rows = [];
        snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
        rows.sort((a, b) => (b.updatedAt?.toMillis?.() || 0) - (a.updatedAt?.toMillis?.() || 0));
        setAudiences(rows);
      }, (err2) => setError(err2));
      return sub;
    });
    return unsub;
  }, []);

  const create = useCallback(async ({ name, filters, matchCount }) => {
    const ref = await addDoc(collection(db, 'eshot_audiences'), {
      name,
      filters,
      matchCount: matchCount ?? 0,
      matchCountUpdatedAt: serverTimestamp(),
      createdBy: email,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return ref.id;
  }, [email]);

  const createGroup = useCallback(async ({ name, contactIds = [] }) => {
    const ids = withMembersAdded([], contactIds);
    const ref = await addDoc(collection(db, 'eshot_audiences'), {
      name,
      kind: 'list',
      contactIds: ids,
      matchCount: ids.length,
      matchCountUpdatedAt: serverTimestamp(),
      createdBy: email,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return ref.id;
  }, [email]);

  // Adding someone back by hand undoes an earlier removal: they leave the
  // remembered list, so a smart group's refresh does not drop them again.
  const addToGroup = useCallback(
    (id, contactIds) => changeGroupMembers(
      id,
      (current) => withMembersAdded(current, contactIds),
      (data) => (data.recipe && typeof data.recipe === 'object' && Array.isArray(data.recipe.removedIds)
        ? { recipe: { ...data.recipe, removedIds: withForgotten(data.recipe.removedIds, contactIds) } }
        : {}),
    ),
    [],
  );

  // A built group remembers who was taken out by hand, so Check for new
  // matches never offers them back. Read and written in the same transaction
  // as the removal, so two overlapping removals both stay recorded.
  const removeFromGroup = useCallback(
    (id, contactIds) => changeGroupMembers(
      id,
      (current) => withMembersRemoved(current, contactIds),
      (data) => (data.recipe && typeof data.recipe === 'object'
        ? { recipe: { ...data.recipe, removedIds: withRemembered(data.recipe.removedIds, contactIds) } }
        : {}),
    ),
    [],
  );

  const renameGroup = useCallback(async (id, name) => {
    await updateDoc(doc(db, 'eshot_audiences', id), { name, updatedAt: serverTimestamp() });
  }, []);

  const removeGroup = useCallback(async (id) => {
    await assertGroupNotInUse(id);
    await deleteDoc(doc(db, 'eshot_audiences', id));
  }, []);

  const update = useCallback(async (id, patch) => {
    await setDoc(doc(db, 'eshot_audiences', id), {
      ...patch,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }, []);

  const remove = useCallback(async (id) => {
    await deleteDoc(doc(db, 'eshot_audiences', id));
  }, []);

  return {
    audiences: audiences || [],
    loading: audiences === null,
    error,
    create,
    createGroup,
    addToGroup,
    removeFromGroup,
    renameGroup,
    removeGroup,
    update,
    remove,
  };
}
